<script setup>
//readme* add imgSrcList to component and call openGallery(index)
import {ref, watch} from 'vue'

const props = defineProps({
  imgSrcList: {
    type:     Array,
    required: true,
    default:  []
  }
})
// Define reactive variables for managing gallery state
let currentPhotoIndex = ref(0)
let isGalleryOpen = ref(false)
let direction = ref('right')//set direction for slider images movement
// Function to handle keyboard events for navigation
const keyUpEventCallback = event => {
  if (event.key === 'ArrowRight') {
    swapRight()
  }
  if (event.key === 'ArrowLeft') {
    swapLeft()
  }
  if (event.key === 'Escape') {
    closeGallery()
  }
}
// Watch for changes in the gallery open state
watch(isGalleryOpen, newValue => {
  if (newValue) {
    window.addEventListener('keyup', keyUpEventCallback)
    document.body.style.overflow = 'hidden';
  } else {
    window.removeEventListener('keyup', keyUpEventCallback)
    document.body.style.overflow = '';
  }
})
// Function to open the gallery at a specific image index
const openGallery = (imgIndex = 0) => {
  currentPhotoIndex.value = imgIndex
  isGalleryOpen.value = true
}
// Function to close the gallery
const closeGallery = () => {
  isGalleryOpen.value = false
}
// Function to move to the next image on right swipe
const swapRight = () => {
  const maxIndex = props.imgSrcList.length
  if (maxIndex === currentPhotoIndex.value + 1) {
    currentPhotoIndex.value = 0
  } else
    currentPhotoIndex.value = currentPhotoIndex.value + 1

  direction.value = 'right'
}
// Function to move to the previous image on left swipe
const swapLeft = () => {
  const lowestIndex = 0
  if (lowestIndex === currentPhotoIndex.value) {
    currentPhotoIndex.value = props.imgSrcList.length - 1
  } else
    currentPhotoIndex.value = currentPhotoIndex.value - 1

  direction.value = 'left'
}
// Function to change the current photo index based on user interaction
const changeCurrentPhotoIndex = newIndex => {
  currentPhotoIndex.value = newIndex
}
// Function to handle touch start event
let startX = 0
let startY = 0
const touchStart = (event) => {
  startX = event.touches[0].clientX
  startY = event.touches[0].clientY
}
// Function to handle touch end event
const touchEnd = (event) => {
  const endX = event.changedTouches[0].clientX
  const endY = event.changedTouches[0].clientY
  const deltaX = endX - startX
  const deltaY = endY - startY
  // Determine swipe direction based on the change in X and Y coordinates
  if (Math.abs(deltaX) > Math.abs(deltaY)) {
    // Horizontal swipe detected
    if (deltaX > 0) {
      swapLeft()
    } else {
      swapRight()
    }
  }
}
defineExpose({openGallery})
</script>
<template>
  <Teleport to="body">
    <div :class="['gallery-wrapper fixed flex top-0 left-0 justify-center items-center w-full h-screen backdrop-blur-[3px] bg-black/50 m-0 p-0 select-none z-[1000000]', { 'open': isGalleryOpen }]" v-bind="$attrs" @touchend="touchEnd"
         @touchstart="touchStart"
         @click.self="closeGallery()">
      <div id="leftArrow" aria-label="Previous" class="arrow-button action-button" @click="swapLeft">
        <svg
            viewBox="0 0 448 512"
            xmlns="http://www.w3.org/2000/svg"
        >
          <path
              d="M447.1 256C447.1 273.7 433.7 288 416 288H109.3l105.4 105.4c12.5 12.5 12.5 32.75 0 45.25C208.4 444.9 200.2 448 192 448s-16.38-3.125-22.62-9.375l-160-160c-12.5-12.5-12.5-32.75 0-45.25l160-160c12.5-12.5 32.75-12.5 45.25 0s12.5 32.75 0 45.25L109.3 224H416C433.7 224 447.1 238.3 447.1 256z"
          />
        </svg>
      </div>
      <div
          v-for="(img, i) in imgSrcList"
          :key="i"
          :class="{ 'current': i === currentPhotoIndex, 'past': i > currentPhotoIndex, 'before': i < currentPhotoIndex }"
          class="image-slide transition-all duration-500 flex items-center justify-center"
      >
        <img :src="img"/>
      </div>
      <div id="rightArrow" aria-label="Next" class="arrow-button action-button" @click="swapRight">
        <svg
            viewBox="0 0 448 512"
            xmlns="http://www.w3.org/2000/svg"
        >
          <path
              d="M438.6 278.6l-160 160C272.4 444.9 264.2 448 256 448s-16.38-3.125-22.62-9.375c-12.5-12.5-12.5-32.75 0-45.25L338.8 288H32C14.33 288 .0016 273.7 .0016 256S14.33 224 32 224h306.8l-105.4-105.4c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0l160 160C451.1 245.9 451.1 266.1 438.6 278.6z"
          />
        </svg>
      </div>
      <div id="closeButton" aria-label="Close" class="action-button" @click="closeGallery">
        <svg
            style="fill: white"
            viewBox="0 0 384 512"
            xmlns="http://www.w3.org/2000/svg"
        >
          <!--! Font Awesome Pro 6.1.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. -->
          <path
              d="M376.6 427.5c11.31 13.58 9.484 33.75-4.094 45.06c-5.984 4.984-13.25 7.422-20.47 7.422c-9.172 0-18.27-3.922-24.59-11.52L192 305.1l-135.4 162.5c-6.328 7.594-15.42 11.52-24.59 11.52c-7.219 0-14.48-2.438-20.47-7.422c-13.58-11.31-15.41-31.48-4.094-45.06l142.9-171.5L7.422 84.5C-3.891 70.92-2.063 50.75 11.52 39.44c13.56-11.34 33.73-9.516 45.06 4.094L192 206l135.4-162.5c11.3-13.58 31.48-15.42 45.06-4.094c13.58 11.31 15.41 31.48 4.094 45.06l-142.9 171.5L376.6 427.5z"
          />
        </svg>
      </div>
      <div id="photoIndexInfo">
        {{ currentPhotoIndex + 1 }}/{{ imgSrcList.length }}
      </div>
      <div class="photo-preview-wrapper">
        <div
            v-for="(img, i) in imgSrcList"
            :key="img"
            class="photo-preview-box"
            @click="changeCurrentPhotoIndex(i)"
        >
          <img :src="img" class="object-contain object-center w-full h-full"/>
        </div>
      </div>
    </div>
  </Teleport>
</template>
<style scoped>
.gallery-wrapper {
  opacity: 0;
  pointer-events: none;
  transform: translateY(100%);
  transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;
}

.gallery-wrapper.open {
  opacity: 1;
  pointer-events: all;
  transform: translateY(0);
}

.image-slide {
  position: absolute;
  min-width: 300px;
  max-width: 70%;
  max-height: 800px;
  pointer-events: none;
}

.image-slide.before {
  opacity: 0;
  transform: translateX(-100%)
}

.image-slide.past {
  opacity: 0;
  transform: translateX(100%)
}

.image-slide.current {
  opacity: 1;
  transform: translateX(0%);
  pointer-events: all;
}

.arrow-button {
  align-self: center;
  position: absolute;
  width: 20px;
  color: rgb(219, 219, 219);
  cursor: pointer;
  z-index: 1000;
}

.action-button path {
  fill: rgb(219, 219, 219);
  transition: fill 0.2s ease;
}

.action-button:hover path {
  fill: white;
}

.photo-preview-wrapper {
  display: flex;
  max-width: 50%;
  overflow-x: auto;
  overflow-y: hidden;
  justify-self: center;
  position: absolute;
  bottom: 20px;
}

.photo-preview-box {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  margin: 0 0.5rem 0 0.5rem;
  cursor: pointer;
}

#leftArrow {
  left: 10px;
}

#rightArrow {
  right: 10px;
}

#closeButton {
  top: 20px;
  right: 20px;
  position: absolute;
  width: 20px;
  color: rgb(219, 219, 219);
  cursor: pointer;
  z-index: 1000;
}

#photoIndexInfo {
  position: absolute;
  align-self: flex-start;
  top: 10px;
  left: 10px;
  color: white;
}
</style>